import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { stringToInt } from "@visioni/a3s-codecs";
import { IanaTimezone } from "@visioni/a3s-dates";
import { AssetCameraKeySchema, SiteIdCodec } from "@visioni/a3s-schema/codecs";
import { FEATURE_NAME } from "../feature-name";
import { FleetClientNoParam, ListOrder, MatchedClientNos } from "./activations";

/**
 * Genuine incidents and their rollups, as `src/incidents/` derived them from
 * the Patriot replica, listed at whichever level of the fleet the caller is
 * looking at.
 *
 * **Support only, and must stay so.** A call is named by its operator note,
 * and call notes carry keyholders' names and numbers. These shapes carry only
 * the note's id, but the detail links straight to the activation holding the
 * note, and `clientNo` is Patriot's own account identity. A customer-facing
 * view needs its own allowlist, not these.
 *
 * ```
 * /features/monitoring/patriot/tenant/incidents[?site=&camera=]
 * /features/monitoring/patriot/tenant/incidents/{incidentId}
 * /features/monitoring/patriot/tenant/incident-summary[?site=&camera=]
 * /features/monitoring/patriot/fleet/incidents[?clientNo=]
 * /features/monitoring/patriot/fleet/incidents/{incidentId}
 * /features/monitoring/patriot/fleet/incident-summary
 * ```
 *
 * Laid out as the activation routes are (`./activations.ts`): a `tenant/` or
 * `fleet/` segment names the scope, and a narrower width is a query filter on
 * the same list. A Patriot client has no a3s tenant, so its filter is on the
 * fleet list, and reads as the fleet activations' does.
 */

export const IncidentCall = z.object({
  /** Patriot's operator note for the call. Who was called stays there. */
  noteId: z.string(),
  at: z.string(),
  /** Outbound calls make an incident; inbound ones only extend one. */
  direction: z.enum(["outbound", "inbound"]),
  /** The activation it was made on; absent for a call outside any. */
  ticketNo: z.string().optional(),
  police: z.boolean(),
});
export type IncidentCall = z.output<typeof IncidentCall>;

/** Why an activation is in the incident: a call was made on it, or it was open near one. */
export const IncidentActivationLink = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("called") }),
  z.object({
    kind: z.literal("nearby"),
    nearest: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("call"), noteId: z.string() }),
      z.object({ kind: z.literal("activation"), ticketNo: z.string() }),
    ]),
    /** 0 when they overlap. */
    gapSeconds: z.number(),
  }),
]);
export type IncidentActivationLink = z.output<typeof IncidentActivationLink>;

/** One activation as the rule saw it. Operators can edit the reason later. */
export const IncidentActivation = z.object({
  ticketNo: z.string(),
  /** With `closedAt`, `typeNo` and `reason`, absent for a ticket a call names but the replica did not hold. */
  openedAt: z.string().optional(),
  /** Absent while it is open. */
  closedAt: z.string().optional(),
  typeNo: z.number().optional(),
  reason: z.string().optional(),
  link: IncidentActivationLink,
  /** It would be a technical activation, but a call makes it part of the incident. */
  technicalOverridden: z.boolean(),
});
export type IncidentActivation = z.output<typeof IncidentActivation>;

/**
 * One row of an incident list. Every time is a UTC instant.
 *
 * The `a3s*` fields are the camera it is counted under, present together or
 * not at all: absent for calls outside any activation and for activations a3s
 * did not mark (Reconeyez, VA), which appear only in the fleet list.
 */
export const IncidentSummary = z.object({
  /** `<clientNo>#<startedAt>`: it changes if a late call moves the start earlier. */
  id: z.string(),
  clientNo: z.string(),
  startedAt: z.string(),
  endedAt: z.string(),
  policeCalled: z.boolean(),
  /** Nothing more can join it. Until then it may still grow or merge. */
  settled: z.boolean(),
  callCount: z.int(),
  activationCount: z.int(),
  a3sTenant: z.string().optional(),
  a3sSite: z.string().optional(),
  a3sAsset: z.string().optional(),
  a3sCamera: z.string().optional(),
});
export type IncidentSummary = z.output<typeof IncidentSummary>;

export const ListIncidentsResponse = z.object({
  items: z.array(IncidentSummary),
  /**
   * Opaque resume token; `null` when the list is exhausted. Pass back verbatim.
   * A fleet page can come back empty with a cursor, as the fleet activation
   * list's can, so page until this is `null`.
   */
  cursor: z.string().nullable(),
});
export type ListIncidentsResponse = z.output<typeof ListIncidentsResponse>;

export const ListFleetIncidentsResponse = ListIncidentsResponse.extend({ clientNos: MatchedClientNos });
export type ListFleetIncidentsResponse = z.output<typeof ListFleetIncidentsResponse>;

/** Narrow by `site`, and by `camera` within it: no partition holds one camera across every site. */
const scopeShape = {
  site: SiteIdCodec.optional(),
  camera: AssetCameraKeySchema.optional(),
};
/** A refinement and its error, spread into `.refine(...)` so every incident query words it the same. */
type Refinement<Q> = [check: (q: Q) => boolean, error: { message: string; path: string[] }];
const cameraNeedsSite: Refinement<{ site?: unknown; camera?: unknown }> = [
  (q) => !(q.camera !== undefined && q.site === undefined),
  { message: "camera cannot be filtered without site", path: ["camera"] },
];
const untilAfterSince: Refinement<{ since?: string | undefined; until?: string | undefined }> = [
  (q) => q.since === undefined || q.until === undefined || Date.parse(q.since) <= Date.parse(q.until),
  { message: "until cannot be before since", path: ["until"] },
];

/**
 * Its own schema rather than the activation list's, so a filter added there
 * does not appear here unasked. `since` / `until` bound when the incident
 * started. Newest first unless `order` is `asc`.
 */
export const ListTenantIncidentsQueryParams = z
  .object({
    limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
    cursor: z.string().optional(),
    since: z.iso.datetime({ offset: true }).optional(),
    until: z.iso.datetime({ offset: true }).optional(),
    order: ListOrder.optional(),
    ...scopeShape,
  })
  .refine(...cameraNeedsSite)
  .refine(...untilAfterSince);
export type ListTenantIncidentsQueryParamsWire = z.input<typeof ListTenantIncidentsQueryParams>;
export type ListTenantIncidentsQueryParams = z.output<typeof ListTenantIncidentsQueryParams>;

export const tenantIncidentsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/incidents",
  scope: "tenant",
});

export const listTenantIncidentsEndpoint = defineEndpoint(tenantIncidentsResource, {
  responseSchema: ListIncidentsResponse,
  querySchema: ListTenantIncidentsQueryParams,
});

/**
 * `since` is required, as on the fleet activation list: the fleet index is
 * walked month by month. `clientNo` narrows to the Patriot clients whose
 * number starts with it, whether or not a3s knows their cameras — see
 * {@link FleetClientNoParam}.
 */
export const ListFleetIncidentsQueryParams = z
  .object({
    limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
    cursor: z.string().optional(),
    since: z.iso.datetime({ offset: true }),
    until: z.iso.datetime({ offset: true }).optional(),
    order: ListOrder.optional(),
    clientNo: FleetClientNoParam,
  })
  .refine(...untilAfterSince);
export type ListFleetIncidentsQueryParamsWire = z.input<typeof ListFleetIncidentsQueryParams>;
export type ListFleetIncidentsQueryParams = z.output<typeof ListFleetIncidentsQueryParams>;

export const fleetIncidentsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/incidents",
  scope: "tenant",
});

export const listFleetIncidentsEndpoint = defineEndpoint(fleetIncidentsResource, {
  responseSchema: ListFleetIncidentsResponse,
  querySchema: ListFleetIncidentsQueryParams,
});

/**
 * Why the incident counts, step by step, from `explainIncident`. A step names
 * its call or activation by id; the detail carries both in full.
 */
export const IncidentStep = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("activationOpened"), at: z.string(), ticketNo: z.string() }),
  z.object({
    kind: z.literal("call"),
    at: z.string(),
    noteId: z.string(),
    /** The first outbound call: the one that makes the incident genuine. */
    madeGenuine: z.boolean(),
  }),
  z.object({ kind: z.literal("activationClosed"), at: z.string(), ticketNo: z.string() }),
  z.object({ kind: z.literal("settled"), at: z.string() }),
]);
export type IncidentStep = z.output<typeof IncidentStep>;

export const IncidentDetail = IncidentSummary.extend({
  /** In time order. */
  calls: z.array(IncidentCall),
  activations: z.array(IncidentActivation),
  /** Oldest first. */
  steps: z.array(IncidentStep),
});
export type IncidentDetail = z.output<typeof IncidentDetail>;

export const GetIncidentPathParams = z.object({ incidentId: z.string().min(1) });
export type GetIncidentPathParams = z.output<typeof GetIncidentPathParams>;

/**
 * Served only when the incident is counted under the tenant in the path, as
 * the tenant activation detail checks its marker.
 */
export const tenantIncidentResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/incidents/{incidentId}",
  scope: "tenant",
  resourcePathSchema: GetIncidentPathParams,
});

export const getTenantIncidentEndpoint = defineEndpoint(tenantIncidentResource, {
  responseSchema: IncidentDetail,
});

/** Any incident by id, whichever tenant it is counted under, or none. */
export const fleetIncidentResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/incidents/{incidentId}",
  scope: "tenant",
  resourcePathSchema: GetIncidentPathParams,
});

export const getFleetIncidentEndpoint = defineEndpoint(fleetIncidentResource, {
  responseSchema: IncidentDetail,
});

const RollupCounts = z.object({
  genuineIncidents: z.int(),
  policeIncidents: z.int(),
  activationsInGenuineIncidents: z.int(),
  nonGenuineActivations: z.int(),
  technicalActivations: z.int(),
  /** Every incident and activation counted has settled, so the counts are final. */
  settled: z.boolean(),
});

export const IncidentPeriodSummary = RollupCounts.extend({
  /** `YYYY-MM-DD` for a day, the Monday's `YYYY-MM-DD` for a week, `YYYY-MM` for a month, in `timeZone`. */
  period: z.string(),
});
export type IncidentPeriodSummary = z.output<typeof IncidentPeriodSummary>;

export const IncidentSummaryResponse = z.object({
  totals: RollupCounts,
  /** Oldest first. A period with nothing counted is absent, not zero. */
  periods: z.array(IncidentPeriodSummary),
});
export type IncidentSummaryResponse = z.output<typeof IncidentSummaryResponse>;

export const IncidentSummaryPeriod = z.enum(["day", "week", "month"]);
export type IncidentSummaryPeriod = z.output<typeof IncidentSummaryPeriod>;

/**
 * A window, and the IANA zone whose days, weeks (from Monday) or months to sum
 * it into. The window is the caller's to align: bound it by local midnights so
 * the first and last periods are whole. `since` is required so a summary never
 * reads a scope's whole history by accident.
 */
const summaryQueryShape = {
  since: z.iso.datetime({ offset: true }),
  until: z.iso.datetime({ offset: true }).optional(),
  /** Checked as the pages check it, so the page and the API refuse the same zones. */
  timeZone: IanaTimezone,
  period: IncidentSummaryPeriod.default("day"),
};
export const FleetIncidentSummaryQueryParams = z.object(summaryQueryShape).refine(...untilAfterSince);
export type FleetIncidentSummaryQueryParamsWire = z.input<typeof FleetIncidentSummaryQueryParams>;
export type FleetIncidentSummaryQueryParams = z.output<typeof FleetIncidentSummaryQueryParams>;

/** Narrowed by `site` and `camera` as the tenant list is. */
export const TenantIncidentSummaryQueryParams = z
  .object({ ...summaryQueryShape, ...scopeShape })
  .refine(...cameraNeedsSite)
  .refine(...untilAfterSince);
export type TenantIncidentSummaryQueryParamsWire = z.input<typeof TenantIncidentSummaryQueryParams>;
export type TenantIncidentSummaryQueryParams = z.output<typeof TenantIncidentSummaryQueryParams>;

export const tenantIncidentSummaryResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/incident-summary",
  scope: "tenant",
});

export const getTenantIncidentSummaryEndpoint = defineEndpoint(tenantIncidentSummaryResource, {
  responseSchema: IncidentSummaryResponse,
  querySchema: TenantIncidentSummaryQueryParams,
});

export const fleetIncidentSummaryResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/incident-summary",
  scope: "tenant",
});

export const getFleetIncidentSummaryEndpoint = defineEndpoint(fleetIncidentSummaryResource, {
  responseSchema: IncidentSummaryResponse,
  querySchema: FleetIncidentSummaryQueryParams,
});
